import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, FormControlLabel, Grid, styled, Typography, useTheme } from '@mui/material';
import { useCheckRTL } from 'hooks/common/useCheckRLF';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  changeDataGapInterval,
  changeVisibleIntervalGap,
  changeVisibleIntervalGapAllLayers,
  gapAnalyticsSelector,
} from 'store/slices/map-view/gapAnalytics';
import { rightBarSelector } from 'store/slices/rightBarSlice';
import { analyticsDisplayedList, TYPE_ANALYTICS_MAP_VIEW } from '../../../../../../common/defines/constants';
import { QUERY_KEY } from '../../../../../../constants/constants';
import { updateInterval } from '../../../../../../services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks';
import { mapViewSelector } from '../../../../../../store/slices/mapViewSlice';
import { SwitchCustom } from '../../../../../Common/SwitchCustom';
import { useLayerSelectCardStyle } from '../../../../MapViewStyle';
import { IIntervalData } from '../../Category/ChartsAnalytics/ChartsAnlytic.type';
import { PolygonLayerTypeRange } from '../PolygonLayerTypeRange';
import SideSurface from '../SideSurface';
import TopSurface from '../TopSurface';

export const GapLayer = ({ labelCard }: any) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const ButtonSubmit = styled(Button)((a) => {
    return {
      width: '100%',
      height: '40px',
      borderRadius: '5px',
      padding: '12px 10px',
      marginBottom: '30px',
      textTransform: 'none',
      border: `1px solid ${theme.palette.primary.main}`,
      color: 'white',
      backgroundColor: `${theme.palette.primary.main}`,

      '&:hover': {
        backgroundColor: '#1C9D57',
        border: `1px solid ${theme.palette.primary.main}`,
        boxShadow: 'none',
      },
      '&:active': {
        boxShadow: 'none',
        backgroundColor: '#1C9D57',
        border: `1px solid ${theme.palette.primary.main}`,
      },
      '&:focus': {
        boxShadow: '0 0 0 0.1rem rgba(70, 255, 70, 0.15)',
        border: `1px solid ${theme.palette.primary.main}`,
      },
    };
  });

  const queryClient = useQueryClient();
  const { isLayer3D, isDefaultInterval } = useAppSelector(mapViewSelector);
  const { dataGap: data, dataGapInterval, isShowAllLayer, chartDataGapPreview } = useAppSelector(gapAnalyticsSelector);
  const classes = useLayerSelectCardStyle();
  const dispatch = useAppDispatch();

  const [isChangeColor, setIsChangeColor] = useState(false);
  const [raster, setRaster] = useState(true);
  const [layerByRange, setLayerByRange] = useState(true);

  const [currentData, setCurrentData] = useState<any>(dataGapInterval);

  useEffect(() => {
    setCurrentData(chartDataGapPreview ? chartDataGapPreview.intervalLimit : dataGapInterval);
  }, [chartDataGapPreview, dataGapInterval]);

  const filterDownload = useMemo(() => {
    const downloadData: any[] = [];
    if (!data) {
      return [];
    }
    const allData = data.map((item: any) => {
      return {
        ...item,
        geometry: {
          type: 'MultiPolygon',
          coordinates: item.data,
        },
      };
    });
    allData.forEach((newData: any) => {
      if (newData.visible) {
        downloadData.push(newData);
      }
    });
    return downloadData;
  }, [data]);

  const handleUpdateIntervals = useMutation((data: any) => updateInterval(currentData._id, data), {
    onSuccess: (response: any) => {
      queryClient.invalidateQueries([QUERY_KEY.CHARTS_GAP_ANALYTIC, isDefaultInterval]).then(() => {});
    },
  });

  const handleChangeColors = (id: number, color: string, typeOfAnalytic: string) => {
    if (currentData?._id && currentData.range) {
      const _data = currentData.range.map((item: IIntervalData) => {
        if (item.from === id) {
          return {
            ...item,
            color,
          };
        }
        return item;
      });
      dispatch(
        changeDataGapInterval({
          ...currentData,
          range: _data,
        })
      );
      setIsChangeColor(true);
      return;
    }
    toast.error('Change settings errors', { toastId: 'changeSettingsError' });
  };

  const { dateAnalyticSelected, levelId } = useAppSelector(mapViewSelector);

  const { analyticName }: { analyticName: any } = useAppSelector(rightBarSelector) || {};
  const isShowLabel = analyticsDisplayedList.includes(analyticName);

  const dataAnalysis: any = queryClient.getQueryData([QUERY_KEY.DATA_ANALYSIS, levelId, dateAnalyticSelected]);

  const amongDataAnalysis = useMemo(() => {
    if (dataAnalysis?.data?.name === 'Stand Count') return dataAnalysis?.data?.length - 1 || 0;
    else return dataAnalysis?.data?.length || 0;
  }, [dataAnalysis]);

  const { isRTL } = useCheckRTL();

  return (
    <>
      {data?.length > 0 && (
        <Grid sx={{ mt: 1 }}>
          <Grid item xs={12} md={12}>
            <Button
              startIcon={layerByRange ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              onClick={() => setLayerByRange(!layerByRange)}
              classes={{ root: classes.buttonTextStyle }}>
              {t('trans.categories')}
            </Button>
            <Collapse in={layerByRange} timeout="auto" unmountOnExit sx={{ px: 2.5 }}>
              <FormControlLabel
                labelPlacement={isRTL ? 'start' : 'end'}
                control={
                  <SwitchCustom
                    checked={isShowAllLayer}
                    onClick={() => dispatch(changeVisibleIntervalGapAllLayers(!isShowAllLayer))}
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <Typography className={classes.selectText}>
                      {isShowAllLayer ? t('trans.unselect_all') : t('trans.select_all')}
                    </Typography>
                  </Box>
                }
              />

              {currentData?.range?.length &&
                currentData.range.map((item: IIntervalData, index: number) => {
                  return (
                    <PolygonLayerTypeRange
                      key={index}
                      label={item.lable}
                      color={item.color}
                      id={item.from}
                      index={index}
                      visible={item.visible}
                      changeColorAnalyticsLayer={handleChangeColors}
                      changeVisibleAnalyticsLayer={changeVisibleIntervalGap}
                      typeOfAnalytic={TYPE_ANALYTICS_MAP_VIEW.GAP}
                    />
                  );
                })}
              {isChangeColor && (
                <Grid container top={2} paddingTop={2} justifyContent={'center'} gap={2} xs={12}>
                  <Grid item xs={4}>
                    <Button
                      color="neutral"
                      variant="outlined"
                      sx={{
                        width: '100%',
                        height: '40px',
                        borderRadius: '5px',
                        p: '12px 10px',
                        mb: '30px',
                        textTransform: 'none',
                        border: `1px solid ${theme.palette.divider}`,
                        color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                      }}
                      onClick={() => {
                        setIsChangeColor(false);
                        queryClient
                          .invalidateQueries([QUERY_KEY.CHARTS_GAP_ANALYTIC, isDefaultInterval])
                          .then(() => {});
                      }}>
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <ButtonSubmit
                      onClick={() => {
                        setIsChangeColor(false);
                        handleUpdateIntervals.mutate(currentData.range);
                      }}>
                      Save
                    </ButtonSubmit>
                  </Grid>
                </Grid>
              )}
            </Collapse>
          </Grid>

          <Grid item xs={12} md={12}>
            {isLayer3D && <SideSurface />}
          </Grid>

          <TopSurface />
        </Grid>
      )}
    </>
  );
};
